import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Package, Palette, Upload, ImagePlus, Loader2, Check, X, FolderPlus, Layers, Pipette, LifeBuoy, GripVertical, Share2, Building2, Search } from "lucide-react";
import { Header } from "@/components/Header";
import { api, mediaUrl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/* pointer-based sortable — works on desktop and touch/iPad */
function useSortable(items, persist) {
  const [order, setOrder] = useState(items.map((i) => i.id));
  const orderRef = useRef(order);
  orderRef.current = order;
  const dragId = useRef(null);
  const containerRef = useRef(null);
  useEffect(() => { setOrder(items.map((i) => i.id)); }, [items]);

  const onGripDown = (id, e) => {
    dragId.current = id;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  };
  const onPointerMove = (e) => {
    if (!dragId.current || !containerRef.current) return;
    const rows = [...containerRef.current.querySelectorAll("[data-sort-id]")];
    const over = rows.find((el) => { const r = el.getBoundingClientRect(); return e.clientY >= r.top && e.clientY <= r.bottom; });
    if (!over) return;
    const overId = over.getAttribute("data-sort-id");
    if (overId === dragId.current) return;
    setOrder((prev) => {
      const from = prev.indexOf(dragId.current); const to = prev.indexOf(overId);
      if (from < 0 || to < 0) return prev;
      const next = [...prev]; const [m] = next.splice(from, 1); next.splice(to, 0, m); return next;
    });
  };
  const onPointerUp = () => {
    if (!dragId.current) return;
    dragId.current = null;
    persist(orderRef.current);
  };
  return { order, containerRef, onGripDown, onPointerMove, onPointerUp };
}

const byId = (arr, id) => arr.find((x) => x.id === id);

export default function Admin() {
  const navigate = useNavigate();
  const { user, company, refresh } = useAuth();
  const isOwner = user.role === "owner";
  const isSuper = !!user.is_superadmin;
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newProduct, setNewProduct] = useState("");
  const logoRef = useRef();

  const loadTree = async () => {
    try {
      const { data } = await api.get("/library/tree");
      setTree(data.products || []);
    } catch (e) {
      toast.error("Couldn't load your library. Please refresh.");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadTree(); }, []);

  const productSort = useSortable(tree, (ids) => api.patch("/products/reorder", { ids }).catch(() => {}));

  const addProduct = async () => {
    const name = newProduct.trim();
    if (!name) return;
    try {
      await api.post("/products", { name });
      setNewProduct("");
      await loadTree();
      toast.success("Service added");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const uploadLogo = async (f) => {
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Logo updated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const requestHelp = async () => {
    try {
      await api.post("/support/request");
      toast.success("Request sent! Our team will reach out in Messages.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[var(--bone)]">
      <Header />
      <div className="mx-auto max-w-5xl px-5 py-8 md:px-8 md:py-12">
        <p className="overline">Library</p>
        <h1 className="mt-1 font-display text-3xl tracking-tight md:text-4xl">Your service library</h1>
        <p className="mt-2 max-w-2xl text-sm text-[var(--ink-soft)]">
          Build your <b className="text-[var(--ink)]">Services</b> (e.g. Cabinet Refacing), add the <b className="text-[var(--ink)]">Features</b> you can change (Door Design, Hardware…), then load the <b className="text-[var(--ink)]">options</b> and <b className="text-[var(--ink)]">colors</b> for each. Building your library is free.
        </p>

        {isOwner && (
          <div className="mt-6 flex flex-wrap items-center gap-4 rounded-xl border border-[var(--line)] bg-white p-4">
            <img src={company.logo_url ? mediaUrl(company.logo_url) : "/customize-logo.jpg"} alt="logo" className="h-16 w-auto max-w-[220px] object-contain" />
            <div className="flex flex-col gap-1">
              <p className="text-sm font-semibold">Company logo</p>
              <button data-testid="upload-logo-btn" onClick={() => logoRef.current?.click()}
                className="flex w-max items-center gap-1.5 rounded-full border border-[var(--line)] px-3 py-1.5 text-xs font-medium hover:bg-[var(--bone)]">
                <Upload className="h-3.5 w-3.5" /> Replace logo
              </button>
              <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--brand)]/30 bg-[var(--brand)]/[0.05] p-4">
          <div className="flex items-center gap-3">
            <LifeBuoy className="h-5 w-5 text-[var(--brand)]" strokeWidth={1.5} />
            <div>
              <p className="text-sm font-semibold">Want us to set up your library for you?</p>
              <p className="text-xs text-[var(--ink-soft)]">Request help and our team will load your services, options and colors.</p>
            </div>
          </div>
          <button data-testid="request-setup-help-btn" onClick={requestHelp}
            className="rounded-full bg-[var(--brand)] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-px hover:bg-[var(--brand-dark)]">
            Request setup help
          </button>
        </div>

        {/* Products */}
        <div className="mt-8 space-y-5">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-[var(--ink-soft)]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : tree.length === 0 ? (
            <div data-testid="library-empty" className="rounded-xl border border-dashed border-[var(--line)] bg-white p-10 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--brand)]/10 text-[var(--brand)]"><Package className="h-7 w-7" strokeWidth={1.5} /></span>
              <h2 className="mt-4 font-display text-2xl tracking-tight">Add your first service</h2>
              <p className="mx-auto mt-2 max-w-sm text-sm text-[var(--ink-soft)]">A service is a folder — like "Cabinet Refacing" or "Countertops". You'll add features and colors inside it.</p>
            </div>
          ) : (
            <div ref={productSort.containerRef} onPointerMove={productSort.onPointerMove} onPointerUp={productSort.onPointerUp} onPointerCancel={productSort.onPointerUp} className="space-y-5">
              {productSort.order.map((id) => {
                const p = byId(tree, id);
                if (!p) return null;
                return (
                  <div key={id} data-sort-id={id}>
                    <ProductCard product={p} onChanged={loadTree} onGripDown={productSort.onGripDown} isSuper={isSuper} />
                  </div>
                );
              })}
            </div>
          )}

          {/* Add product */}
          <div className="flex items-center gap-2 rounded-xl border border-dashed border-[var(--line)] bg-white p-4">
            <FolderPlus className="h-5 w-5 shrink-0 text-[var(--brand)]" />
            <input data-testid="new-product-input" value={newProduct} onChange={(e) => setNewProduct(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addProduct(); }}
              placeholder="New service name — e.g. Cabinet Refacing"
              className="min-w-0 flex-1 rounded-lg border border-[var(--line)] bg-[var(--bone)] px-3 py-2.5 text-sm outline-none focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20" />
            <button data-testid="add-product-btn" onClick={addProduct}
              className="flex items-center gap-1.5 rounded-full bg-[var(--ink)] px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90">
              <Plus className="h-4 w-4" /> Add service
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Export service to another company (superadmin) ---------------- */
function ExportModal({ product, onClose }) {
  const [companies, setCompanies] = useState(null);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/companies"); setCompanies(data || []); }
      catch (e) { toast.error("Couldn't load companies."); setCompanies([]); }
    })();
  }, []);

  const doExport = async (c) => {
    setBusyId(c.id);
    try {
      const { data } = await api.post(`/products/${product.id}/export`, { target_company_id: c.id });
      toast.success(`Copied "${product.name}" to ${c.name} — ${data.features} features, ${data.options} options, ${data.colors} colors`);
      onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Export failed."); setBusyId(null); }
  };

  const filtered = (companies || []).filter((c) => c.name?.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="export-modal" className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[var(--line)] px-5 py-4">
          <div>
            <p className="overline">Export service</p>
            <h3 className="font-display text-xl tracking-tight">Copy "{product.name}" to…</h3>
          </div>
          <button data-testid="export-close" onClick={onClose} className="rounded-full p-2 text-[var(--ink-soft)] hover:bg-[var(--bone)]"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5">
          <p className="mb-3 text-xs text-[var(--ink-soft)]">Clones every feature, option, color and availability into the company you pick. The original stays untouched.</p>
          <div className="relative mb-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--ink-soft)]" />
            <input data-testid="export-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search companies…"
              className="w-full rounded-lg border border-[var(--line)] bg-[var(--bone)] py-2.5 pl-9 pr-3 text-sm outline-none focus:border-[var(--brand)]" />
          </div>
          <div className="max-h-72 space-y-1 overflow-y-auto">
            {companies === null ? (
              <div className="flex items-center gap-2 p-3 text-sm text-[var(--ink-soft)]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
            ) : filtered.length === 0 ? (
              <p className="p-3 text-sm text-[var(--ink-soft)]">No companies found.</p>
            ) : filtered.map((c) => (
              <button key={c.id} data-testid={`export-company-${c.id}`} onClick={() => doExport(c)} disabled={busyId}
                className="flex w-full items-center gap-3 rounded-lg border border-[var(--line)] px-3 py-2.5 text-left transition-colors hover:border-[var(--brand)] hover:bg-[var(--brand)]/[0.04] disabled:opacity-50">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--surface-2)] text-[var(--ink-soft)]"><Building2 className="h-4.5 w-4.5" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold">{c.name}</span>
                  <span className="block text-xs text-[var(--ink-soft)]">{c.users} member{c.users === 1 ? "" : "s"}</span>
                </span>
                {busyId === c.id ? <Loader2 className="h-4 w-4 animate-spin text-[var(--brand)]" /> : <Share2 className="h-4 w-4 text-[var(--ink-soft)]" />}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Product ---------------- */
function ProductCard({ product, onChanged, onGripDown, isSuper }) {
  const [renaming, setRenaming] = useState(false);
  const [val, setVal] = useState(product.name);
  const [newFeat, setNewFeat] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const featureSort = useSortable(product.features, (ids) => api.patch("/features/reorder", { ids }).catch(() => {}));

  const saveRename = async () => {
    const v = val.trim();
    setRenaming(false);
    if (!v || v === product.name) { setVal(product.name); return; }
    try { await api.patch(`/products/${product.id}`, { name: v }); onChanged(); toast.success("Service renamed"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setVal(product.name); }
  };
  const del = async () => {
    if (!window.confirm(`Delete "${product.name}" and everything inside it?`)) return;
    try { await api.delete(`/products/${product.id}`); onChanged(); toast.success("Service deleted"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const addFeature = async () => {
    const name = newFeat.trim();
    if (!name) return;
    try { await api.post("/features", { product_id: product.id, name }); setNewFeat(""); onChanged(); toast.success("Feature added"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid={`product-card-${product.id}`} className="rounded-2xl border border-[var(--line)] bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2">
        <span data-testid={`product-grip-${product.id}`} onPointerDown={(e) => onGripDown?.(product.id, e)} title="Drag to reorder"
          className="cursor-grab touch-none p-1 text-[var(--ink-soft)]/60 hover:text-[var(--ink-soft)]"><GripVertical className="h-5 w-5" /></span>
        <Package className="h-6 w-6 shrink-0 text-[var(--brand)]" strokeWidth={1.75} />
        {renaming ? (
          <input data-testid={`product-rename-${product.id}`} autoFocus value={val} onChange={(e) => setVal(e.target.value)}
            onBlur={saveRename} onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") { setRenaming(false); setVal(product.name); } }}
            className="min-w-0 flex-1 rounded-lg border border-[var(--brand)] bg-white px-3 py-1.5 font-display text-2xl tracking-tight outline-none" />
        ) : (
          <h2 className="min-w-0 flex-1 truncate font-display text-2xl tracking-tight">{product.name}</h2>
        )}
        <button data-testid={`product-rename-btn-${product.id}`} onClick={() => { setVal(product.name); setRenaming(true); }} title="Rename service"
          className="rounded-full p-2 text-[var(--ink-soft)] hover:bg-[var(--bone)]"><Pencil className="h-4 w-4" /></button>
        {isSuper && (
          <button data-testid={`product-export-btn-${product.id}`} onClick={() => setExportOpen(true)} title="Export this service to another company"
            className="flex items-center gap-1.5 rounded-full border border-[var(--brand)] px-3 py-2 text-xs font-semibold text-[var(--brand)] hover:bg-[var(--brand)]/[0.06]"><Share2 className="h-3.5 w-3.5" /> Export</button>
        )}
        <button data-testid={`product-delete-btn-${product.id}`} onClick={del} title="Delete service"
          className="rounded-full p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
      </div>
      {exportOpen && <ExportModal product={product} onClose={() => setExportOpen(false)} />}

      <div className="mt-4 space-y-4 border-l-2 border-[var(--line)] pl-4">
        <div ref={featureSort.containerRef} onPointerMove={featureSort.onPointerMove} onPointerUp={featureSort.onPointerUp} onPointerCancel={featureSort.onPointerUp} className="space-y-4">
          {featureSort.order.map((id) => {
            const f = byId(product.features, id);
            if (!f) return null;
            return (
              <div key={id} data-sort-id={id}>
                <FeatureCard feature={f} onChanged={onChanged} onGripDown={featureSort.onGripDown} />
              </div>
            );
          })}
        </div>
        {product.features.length === 0 && (
          <p className="text-xs text-[var(--ink-soft)]">No features yet. Add one below — e.g. "Door Design" or "Hardware".</p>
        )}
        <div className="flex items-center gap-2">
          <input data-testid={`new-feature-input-${product.id}`} value={newFeat} onChange={(e) => setNewFeat(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addFeature(); }}
            placeholder="New feature — e.g. Door Design"
            className="min-w-0 flex-1 rounded-lg border border-[var(--line)] bg-[var(--bone)] px-3 py-2 text-sm outline-none focus:border-[var(--brand)]" />
          <button data-testid={`add-feature-btn-${product.id}`} onClick={addFeature}
            className="flex items-center gap-1.5 rounded-full border border-[var(--brand)] px-4 py-2 text-sm font-semibold text-[var(--brand)] hover:bg-[var(--brand)]/[0.06]">
            <Plus className="h-4 w-4" /> Feature
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Feature ---------------- */
function FeatureCard({ feature, onChanged, onGripDown }) {
  const [renaming, setRenaming] = useState(false);
  const [val, setVal] = useState(feature.name);

  const saveRename = async () => {
    const v = val.trim();
    setRenaming(false);
    if (!v || v === feature.name) { setVal(feature.name); return; }
    try { await api.patch(`/features/${feature.id}`, { name: v }); onChanged(); toast.success("Feature renamed"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setVal(feature.name); }
  };
  const del = async () => {
    if (!window.confirm(`Delete feature "${feature.name}" and its options & colors?`)) return;
    try { await api.delete(`/features/${feature.id}`); onChanged(); toast.success("Feature deleted"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid={`feature-card-${feature.id}`} className="rounded-xl border border-[var(--line)] bg-[var(--bone)]/60 p-4">
      <div className="flex items-center gap-2">
        <span data-testid={`feature-grip-${feature.id}`} onPointerDown={(e) => onGripDown?.(feature.id, e)} title="Drag to reorder"
          className="cursor-grab touch-none p-0.5 text-[var(--ink-soft)]/60 hover:text-[var(--ink-soft)]"><GripVertical className="h-4 w-4" /></span>
        <Layers className="h-4 w-4 shrink-0 text-[var(--ink-soft)]" />
        {renaming ? (
          <input data-testid={`feature-rename-${feature.id}`} autoFocus value={val} onChange={(e) => setVal(e.target.value)}
            onBlur={saveRename} onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") { setRenaming(false); setVal(feature.name); } }}
            className="min-w-0 flex-1 rounded-lg border border-[var(--brand)] bg-white px-2 py-1 text-base font-semibold outline-none" />
        ) : (
          <h3 className="min-w-0 flex-1 truncate text-base font-semibold">{feature.name}</h3>
        )}
        <button data-testid={`feature-rename-btn-${feature.id}`} onClick={() => { setVal(feature.name); setRenaming(true); }} title="Rename feature"
          className="rounded-full p-1.5 text-[var(--ink-soft)] hover:bg-white"><Pencil className="h-3.5 w-3.5" /></button>
        <button data-testid={`feature-delete-btn-${feature.id}`} onClick={del} title="Delete feature"
          className="rounded-full p-1.5 text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ColorPanel feature={feature} onChanged={onChanged} />
        <OptionPanel feature={feature} onChanged={onChanged} />
      </div>
    </div>
  );
}

/* ---------------- Colors panel ---------------- */
function ColorPanel({ feature, onChanged }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [hex, setHex] = useState("#8a6d4a");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const imgRef = useRef();
  const canvasRef = useRef();

  const hasEyeDropper = typeof window !== "undefined" && "EyeDropper" in window;

  const onFile = (f) => { if (!f) return; setFile(f); setPreview(URL.createObjectURL(f)); };

  const pickFromScreen = async () => {
    try {
      // eslint-disable-next-line no-undef
      const ed = new window.EyeDropper();
      const res = await ed.open();
      if (res?.sRGBHex) setHex(res.sRGBHex);
    } catch { /* cancelled */ }
  };

  // sample color from the uploaded reference photo when clicked
  const sampleFromImage = (e) => {
    const img = imgRef.current;
    if (!img) return;
    const canvas = canvasRef.current;
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    const rect = img.getBoundingClientRect();
    const x = Math.round((e.clientX - rect.left) / rect.width * img.naturalWidth);
    const y = Math.round((e.clientY - rect.top) / rect.height * img.naturalHeight);
    try {
      const d = ctx.getImageData(x, y, 1, 1).data;
      const h = "#" + [d[0], d[1], d[2]].map((v) => v.toString(16).padStart(2, "0")).join("");
      setHex(h);
      toast.success(`Picked ${h}`);
    } catch { toast.error("Couldn't read that pixel."); }
  };

  const add = async () => {
    if (!name.trim()) return toast.error("Give the color a name.");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("hex", hex);
      if (file) fd.append("file", file);
      await api.post(`/features/${feature.id}/colors`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setName(""); setFile(null); setPreview(null); setOpen(false);
      onChanged();
      toast.success("Color added");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/colors/${id}`); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="rounded-lg border border-[var(--line)] bg-white p-3">
      <p className="overline mb-2 flex items-center gap-1"><Palette className="h-3.5 w-3.5" /> Colors & finishes</p>
      <div className="flex flex-wrap gap-2">
        {feature.colors.map((c) => (
          <div key={c.id} data-testid={`color-chip-${c.id}`} className="group flex items-center gap-1.5 rounded-full border border-[var(--line)] py-1 pl-1 pr-2">
            {c.image_url
              ? <img src={mediaUrl(c.image_url)} alt={c.name} className="h-5 w-5 rounded-full object-cover" />
              : <span className="h-5 w-5 rounded-full ring-1 ring-[var(--line)]" style={{ backgroundColor: c.hex || "#ddd" }} />}
            <span className="text-xs font-medium">{c.name}</span>
            <button data-testid={`color-delete-${c.id}`} onClick={() => del(c.id)} className="text-[var(--ink-soft)] opacity-0 transition-opacity group-hover:opacity-100 hover:text-red-500"><X className="h-3 w-3" /></button>
          </div>
        ))}
        {feature.colors.length === 0 && <span className="text-xs text-[var(--ink-soft)]">No colors yet.</span>}
      </div>

      {open ? (
        <div className="mt-3 space-y-2 rounded-lg border border-[var(--line)] bg-[var(--bone)]/60 p-3">
          <input data-testid={`color-name-${feature.id}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Color name — e.g. Navy"
            className="w-full rounded-lg border border-[var(--line)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--brand)]" />
          <div className="flex items-center gap-2">
            <input data-testid={`color-hex-picker-${feature.id}`} type="color" value={hex} onChange={(e) => setHex(e.target.value)} className="h-9 w-12 cursor-pointer rounded border border-[var(--line)] bg-white" />
            <input value={hex} onChange={(e) => setHex(e.target.value)} className="w-28 rounded-lg border border-[var(--line)] bg-white px-2 py-2 text-sm outline-none focus:border-[var(--brand)]" />
            {hasEyeDropper && (
              <button data-testid={`color-eyedropper-${feature.id}`} onClick={pickFromScreen} title="Pick a color from anywhere on screen"
                className="flex items-center gap-1 rounded-lg border border-[var(--line)] bg-white px-2.5 py-2 text-xs font-medium hover:bg-[var(--bone)]"><Pipette className="h-4 w-4" /> Screen</button>
            )}
          </div>
          <div>
            <label className="text-xs text-[var(--ink-soft)]">Reference photo (optional) — click the photo to grab its color</label>
            <input data-testid={`color-file-${feature.id}`} type="file" accept="image/*" onChange={(e) => onFile(e.target.files?.[0])}
              className="mt-1 block w-full text-xs text-[var(--ink-soft)] file:mr-2 file:rounded-full file:border-0 file:bg-[var(--surface-2)] file:px-3 file:py-1.5 file:text-xs file:font-medium hover:file:bg-[var(--line)]" />
            {preview && (
              <img ref={imgRef} src={preview} alt="ref" onClick={sampleFromImage} crossOrigin="anonymous"
                className="mt-2 max-h-40 w-full cursor-crosshair rounded-lg border border-[var(--line)] object-contain" />
            )}
            <canvas ref={canvasRef} className="hidden" />
          </div>
          <div className="flex gap-2">
            <button data-testid={`color-save-${feature.id}`} onClick={add} disabled={saving}
              className="flex items-center gap-1.5 rounded-full bg-[var(--brand)] px-4 py-2 text-xs font-semibold text-white hover:bg-[var(--brand-dark)] disabled:opacity-60">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save color
            </button>
            <button onClick={() => { setOpen(false); setPreview(null); setFile(null); }} className="rounded-full border border-[var(--line)] px-4 py-2 text-xs font-medium hover:bg-[var(--bone)]">Cancel</button>
          </div>
        </div>
      ) : (
        <button data-testid={`add-color-btn-${feature.id}`} onClick={() => setOpen(true)}
          className="mt-3 flex items-center gap-1.5 rounded-full border border-dashed border-[var(--line)] px-3 py-1.5 text-xs font-semibold text-[var(--ink-soft)] hover:border-[var(--brand)] hover:text-[var(--brand)]">
          <Plus className="h-3.5 w-3.5" /> Add color
        </button>
      )}
    </div>
  );
}

/* ---------------- Options panel ---------------- */
function OptionPanel({ feature, onChanged }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const optionSort = useSortable(feature.options, (ids) => api.patch("/foptions/reorder", { ids }).catch(() => {}));

  const onFile = (f) => { if (!f) return; setFile(f); setPreview(URL.createObjectURL(f)); };

  const add = async () => {
    if (!name.trim()) return toast.error("Give the option a name.");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      if (file) fd.append("file", file);
      await api.post(`/features/${feature.id}/options`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setName(""); setFile(null); setPreview(null); setOpen(false);
      onChanged();
      toast.success("Option added");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-[var(--line)] bg-white p-3">
      <p className="overline mb-2 flex items-center gap-1"><Package className="h-3.5 w-3.5" /> Options</p>
      <div ref={optionSort.containerRef} onPointerMove={optionSort.onPointerMove} onPointerUp={optionSort.onPointerUp} onPointerCancel={optionSort.onPointerUp} className="space-y-2">
        {optionSort.order.map((id) => {
          const o = byId(feature.options, id);
          if (!o) return null;
          return (
            <div key={id} data-sort-id={id}>
              <OptionRow option={o} colors={feature.colors} onChanged={onChanged} onGripDown={optionSort.onGripDown} />
            </div>
          );
        })}
        {feature.options.length === 0 && <span className="text-xs text-[var(--ink-soft)]">No options yet.</span>}
      </div>

      {open ? (
        <div className="mt-3 space-y-2 rounded-lg border border-[var(--line)] bg-[var(--bone)]/60 p-3">
          <input data-testid={`option-name-${feature.id}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Option name — e.g. Shades"
            className="w-full rounded-lg border border-[var(--line)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--brand)]" />
          <input data-testid={`option-file-${feature.id}`} type="file" accept="image/*" onChange={(e) => onFile(e.target.files?.[0])}
            className="block w-full text-xs text-[var(--ink-soft)] file:mr-2 file:rounded-full file:border-0 file:bg-[var(--surface-2)] file:px-3 file:py-1.5 file:text-xs file:font-medium hover:file:bg-[var(--line)]" />
          {preview && <img src={preview} alt="ref" className="max-h-32 w-full rounded-lg border border-[var(--line)] object-contain" />}
          <div className="flex gap-2">
            <button data-testid={`option-save-${feature.id}`} onClick={add} disabled={saving}
              className="flex items-center gap-1.5 rounded-full bg-[var(--brand)] px-4 py-2 text-xs font-semibold text-white hover:bg-[var(--brand-dark)] disabled:opacity-60">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save option
            </button>
            <button onClick={() => { setOpen(false); setPreview(null); setFile(null); }} className="rounded-full border border-[var(--line)] px-4 py-2 text-xs font-medium hover:bg-[var(--bone)]">Cancel</button>
          </div>
        </div>
      ) : (
        <button data-testid={`add-option-btn-${feature.id}`} onClick={() => setOpen(true)}
          className="mt-3 flex items-center gap-1.5 rounded-full border border-dashed border-[var(--line)] px-3 py-1.5 text-xs font-semibold text-[var(--ink-soft)] hover:border-[var(--brand)] hover:text-[var(--brand)]">
          <Plus className="h-3.5 w-3.5" /> Add option
        </button>
      )}
    </div>
  );
}

/* ---------------- Option row (with color availability) ---------------- */
function OptionRow({ option, colors, onChanged, onGripDown }) {
  const [renaming, setRenaming] = useState(false);
  const [val, setVal] = useState(option.name);
  const [expanded, setExpanded] = useState(false);
  const [avail, setAvail] = useState(option.available_color_ids || []);
  const [savingColors, setSavingColors] = useState(false);

  useEffect(() => { setAvail(option.available_color_ids || []); }, [option.available_color_ids]);

  const saveRename = async () => {
    const v = val.trim();
    setRenaming(false);
    if (!v || v === option.name) { setVal(option.name); return; }
    try { await api.patch(`/foptions/${option.id}`, { name: v }); onChanged(); toast.success("Option renamed"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setVal(option.name); }
  };
  const del = async () => {
    try { await api.delete(`/foptions/${option.id}`); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleColor = async (colorId) => {
    const next = avail.includes(colorId) ? avail.filter((x) => x !== colorId) : [...avail, colorId];
    setAvail(next);
    setSavingColors(true);
    try { await api.patch(`/foptions/${option.id}/colors`, { color_ids: next }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setAvail(option.available_color_ids || []); }
    finally { setSavingColors(false); }
  };

  return (
    <div data-testid={`option-row-${option.id}`} className="rounded-lg border border-[var(--line)] bg-white">
      <div className="flex items-center gap-2 p-2">
        <span data-testid={`option-grip-${option.id}`} onPointerDown={(e) => onGripDown?.(option.id, e)} title="Drag to reorder"
          className="cursor-grab touch-none p-0.5 text-[var(--ink-soft)]/60 hover:text-[var(--ink-soft)]"><GripVertical className="h-4 w-4" /></span>
        <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-[var(--line)] bg-[var(--bone)]">
          {option.image_url ? <img src={mediaUrl(option.image_url)} alt={option.name} className="h-full w-full object-cover" /> : <ImagePlus className="h-4 w-4 text-[var(--ink-soft)]" />}
        </div>
        {renaming ? (
          <input data-testid={`option-rename-${option.id}`} autoFocus value={val} onChange={(e) => setVal(e.target.value)}
            onBlur={saveRename} onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") { setRenaming(false); setVal(option.name); } }}
            className="min-w-0 flex-1 rounded border border-[var(--brand)] bg-white px-2 py-1 text-sm font-medium outline-none" />
        ) : (
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{option.name}</span>
        )}
        <button data-testid={`option-colors-toggle-${option.id}`} onClick={() => setExpanded((v) => !v)}
          className="rounded-full border border-[var(--line)] px-2 py-1 text-[10px] font-semibold text-[var(--ink-soft)] hover:bg-[var(--bone)]">
          {avail.length}/{colors.length} colors
        </button>
        <button data-testid={`option-rename-btn-${option.id}`} onClick={() => { setVal(option.name); setRenaming(true); }} className="rounded-full p-1.5 text-[var(--ink-soft)] hover:bg-[var(--bone)]"><Pencil className="h-3.5 w-3.5" /></button>
        <button data-testid={`option-delete-btn-${option.id}`} onClick={del} className="rounded-full p-1.5 text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
      {expanded && (
        <div className="border-t border-[var(--line)] p-2">
          <p className="mb-1.5 text-[11px] text-[var(--ink-soft)]">Tick the colors available for <b className="text-[var(--ink)]">{option.name}</b>{savingColors ? " · saving…" : ""}</p>
          {colors.length === 0 ? (
            <p className="text-xs text-[var(--ink-soft)]">Add colors first (left panel).</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {colors.map((c) => {
                const on = avail.includes(c.id);
                return (
                  <button key={c.id} data-testid={`option-color-${option.id}-${c.id}`} onClick={() => toggleColor(c.id)}
                    className={`flex items-center gap-1.5 rounded-full border py-1 pl-1 pr-2 text-xs transition-colors ${on ? "border-[var(--brand)] bg-[var(--brand)]/[0.08] font-semibold" : "border-[var(--line)] text-[var(--ink-soft)]"}`}>
                    {c.image_url
                      ? <img src={mediaUrl(c.image_url)} alt={c.name} className="h-4 w-4 rounded-full object-cover" />
                      : <span className="h-4 w-4 rounded-full ring-1 ring-[var(--line)]" style={{ backgroundColor: c.hex || "#ddd" }} />}
                    {c.name}
                    {on && <Check className="h-3 w-3 text-[var(--brand)]" strokeWidth={3} />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
